import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useU2000AxiosAuthenticated from '../../u2000Core/axios/U2000UseAxiosAuthenticated';
import { U2000HandleApiError, type U2000ApiErrorResult } from '../../u2000Core/axios/U2000HandleApiError';
import { useToast } from '../../u2000Core/context/toast/U2000ToastContext';
import { NS } from '../U2106';
import {
  doGetMfaStatus,
  doMfaDisable,
  doMfaEnrollConfirm,
  doMfaEnrollStart,
  doMfaRegenerateBackupCodes,
  luHandleAppiError,
  type U2106MfaEnrollStartResult,
  type U2106MfaProofPayload,
  type U2106MfaStatusResult,
} from '../U2106Service';

export type UseU2106MfaHandlersReturn = {
  loading: boolean;
  status: U2106MfaStatusResult | null;
  loadStatus: () => Promise<U2106MfaStatusResult | null>;
  enrollStart: () => Promise<U2106MfaEnrollStartResult | null>;
  enrollConfirm: (code: string) => Promise<string[] | null>;
  disable: (payload: U2106MfaProofPayload) => Promise<boolean>;
  regenerateBackupCodes: (payload: U2106MfaProofPayload) => Promise<string[] | null>;
};

function toastApiErrors(
  error: unknown,
  t: (key: string, options?: Record<string, unknown>) => string,
  toast: { error: (text: string, title?: string) => void },
  fallbackKey: string,
): void {
  const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError) as U2000ApiErrorResult;
  if (errorResult.genericErrors?.length) {
    errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
  } else {
    toast.error(t(fallbackKey, { ns: NS }));
  }
}

export function useU2106MfaHandlers(): UseU2106MfaHandlersReturn {
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation(NS);
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<U2106MfaStatusResult | null>(null);

  const loadStatus = useCallback(async (): Promise<U2106MfaStatusResult | null> => {
    setLoading(true);
    try {
      const response = await doGetMfaStatus(axios);
      const result = response.data.result;
      setStatus(result);
      return result;
    } catch (error: unknown) {
      toastApiErrors(error, t, toast, 'myAccount.mfa.errors.generic');
      return null;
    } finally {
      setLoading(false);
    }
  }, [axios, t, toast]);

  const enrollStart = useCallback(async (): Promise<U2106MfaEnrollStartResult | null> => {
    setLoading(true);
    try {
      const response = await doMfaEnrollStart(axios);
      return response.data.result;
    } catch (error: unknown) {
      toastApiErrors(error, t, toast, 'myAccount.mfa.errors.generic');
      return null;
    } finally {
      setLoading(false);
    }
  }, [axios, t, toast]);

  const enrollConfirm = useCallback(
    async (code: string): Promise<string[] | null> => {
      setLoading(true);
      try {
        const response = await doMfaEnrollConfirm(axios, { code: code.trim() });
        const codes = response.data.result.backupCodes ?? [];
        setStatus({ enabled: true, enrolled: true, enrolledAt: new Date().toISOString(), applicable: true });
        toast.success(t('myAccount.mfa.enableSuccess', { ns: NS }));
        return codes;
      } catch (error: unknown) {
        toastApiErrors(error, t, toast, 'myAccount.mfa.errors.generic');
        return null;
      } finally {
        setLoading(false);
      }
    },
    [axios, t, toast],
  );

  const disable = useCallback(
    async (payload: U2106MfaProofPayload): Promise<boolean> => {
      setLoading(true);
      try {
        await doMfaDisable(axios, payload);
        setStatus({ enabled: false, enrolled: false, enrolledAt: null, applicable: true });
        toast.success(t('myAccount.mfa.disableSuccess', { ns: NS }));
        return true;
      } catch (error: unknown) {
        toastApiErrors(error, t, toast, 'myAccount.mfa.errors.generic');
        return false;
      } finally {
        setLoading(false);
      }
    },
    [axios, t, toast],
  );

  const regenerateBackupCodes = useCallback(
    async (payload: U2106MfaProofPayload): Promise<string[] | null> => {
      setLoading(true);
      try {
        const response = await doMfaRegenerateBackupCodes(axios, payload);
        toast.success(t('myAccount.mfa.regenerateSuccess', { ns: NS }));
        return response.data.result.backupCodes ?? [];
      } catch (error: unknown) {
        toastApiErrors(error, t, toast, 'myAccount.mfa.errors.generic');
        return null;
      } finally {
        setLoading(false);
      }
    },
    [axios, t, toast],
  );

  return {
    loading,
    status,
    loadStatus,
    enrollStart,
    enrollConfirm,
    disable,
    regenerateBackupCodes,
  };
}
